/**
 * Lazy hover-video controller for product and collection cards.
 * Loads sources on first mouse hover only; respects reduced motion.
 */

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {HoverVideo | null} */
let activeHoverVideo = null;

export class HoverVideo extends HTMLElement {
  /** @type {boolean} */
  #loaded = false;

  /** @type {HTMLVideoElement | null} */
  get video() {
    return this.querySelector('video');
  }

  /**
   * Inject <source> elements from data-sources once.
   */
  ensureSources() {
    if (this.#loaded) return;
    const video = this.video;
    if (!video) return;

    let sources = [];
    try {
      sources = JSON.parse(this.dataset.sources || '[]');
    } catch {
      sources = [];
    }

    if (!Array.isArray(sources) || sources.length === 0) return;

    const ordered = [...sources].sort((a, b) => {
      const aWebm = String(a?.type || '').includes('webm') ? 0 : 1;
      const bWebm = String(b?.type || '').includes('webm') ? 0 : 1;
      return aWebm - bWebm;
    });

    for (const item of ordered) {
      if (!item?.src) continue;
      const source = document.createElement('source');
      source.src = item.src;
      if (item.type) source.type = item.type;
      video.appendChild(source);
    }

    video.load();
    this.#loaded = true;
  }

  /**
   * Pause any other active hover video, then play this one.
   * @returns {Promise<void>}
   */
  async play() {
    if (prefersReducedMotion()) return;

    this.ensureSources();
    const video = this.video;
    if (!video) return;

    if (activeHoverVideo && activeHoverVideo !== this) {
      activeHoverVideo.pause();
    }
    activeHoverVideo = this;

    video.muted = true;
    this.setAttribute('data-active', '');

    try {
      await video.play();
    } catch {
      this.removeAttribute('data-active');
    }
  }

  /**
   * Pause and reset playback head.
   */
  pause() {
    const video = this.video;
    if (!video) return;

    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // Ignore seek errors before metadata
    }
    this.removeAttribute('data-active');

    if (activeHoverVideo === this) {
      activeHoverVideo = null;
    }
  }
}

if (!customElements.get('hover-video')) {
  customElements.define('hover-video', HoverVideo);
}

/**
 * Collection / resource card host: plays hover-video on mouse enter.
 */
export class HoverVideoHost extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.#hoverVideo?.pause();
  }

  /** @returns {HoverVideo | null} */
  get #hoverVideo() {
    return this.querySelector('hover-video');
  }

  /**
   * @param {PointerEvent} event
   */
  #onEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (prefersReducedMotion()) return;
    this.#hoverVideo?.play();
  };

  /**
   * @param {PointerEvent} event
   */
  #onLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hoverVideo?.pause();
  };
}

if (!customElements.get('hover-video-host')) {
  customElements.define('hover-video-host', HoverVideoHost);
}
